import React from "react";
import {
    Form,
    TextInput,
    required,
    SaveButton,
    FileInput,
    FileField,
    useDelete,
    regex,
    useTranslate,
    usePermissions,
    useTheme,
} from "react-admin";
import { Button, Box } from "@mui/material";
import { RichTextInput } from "ra-input-rich-text";
import DeleteIcon from "@mui/icons-material/Delete";
import { RichtextInputActionBar } from "./RichtextInputActionBar";

export const EditView = ({
    createMedia,
    mediaTools,
    setMediaTools,
    mediaImages = [],
    isFullscreen = false,
}) => {
    const translate = useTranslate();
    const [theme] = useTheme();
    const { isLoading, permissions } = usePermissions();
    const [deleteOne] = useDelete("media", {
        id: mediaTools.record?.id,
        previousData: mediaTools.record,
    });

    const deleteMedia = (e) => {
        deleteOne();
        setMediaTools({
            edit: null,
            type: null,
            id: null,
            record: null,
        });
    };

    if (isLoading) {
        return <></>;
    }
    return (
        <Form onSubmit={createMedia} sx={{}}>
            <Box
                sx={{
                    pt: 1,
                    px: 4,
                    mt: 2,
                    ...(theme === "dark" && { background: "#1e1e1e" }),
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0
                }}>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: isFullscreen ? 0 : "10rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        overflowY: isFullscreen ? "auto" : "visible",
                        pr: isFullscreen ? 1 : 0,
                        maxWidth: "1350px",
                        mx: "auto",
                        px: 5
                    }}
                >
                    {mediaTools.type !== "file" && (
                        <TextInput
                            title={translate("equip.title")}
                            label="equip.title"
                            source="shortname"
                            validate={[required()]}
                            fullWidth
                            defaultValue={mediaTools.record?.shortname}
                        />
                    )}
                    {mediaTools.type === "file" && !mediaTools.id && (
                        <FileInput
                            source="files"
                            title={translate("equip.allowedFileTypes")}
                            label="equip.allowedFileTypes"
                            accept={{
                                "image/jpeg": [".jpeg"],
                                "image/png": [".png"],
                                "image/gif": [".gif"],
                                "application/pdf": [".pdf"],
                            }}
                            multiple={false}
                            maxSize={20971520}
                            required
                        >
                            <FileField source="src" title="title" />
                        </FileInput>
                    )}

                    {mediaTools.type === "link" && (
                        <TextInput
                            title={translate("equip.insertLink")}
                            label="equip.insertLink"
                            source="url"
                            validate={[
                                required(),
                                /* eslint-disable no-useless-escape */
                                regex(
                                    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
                                    "Must be a valid url"
                                ),
                            ]}
                            fullWidth
                            defaultValue={mediaTools.record && mediaTools.record.url}
                        />
                    )}

                    {mediaTools.type === "text" && (
                        <Box
                            sx={{
                                flex: isFullscreen ? 1 : "none",
                                display: "flex",
                                flexDirection: "column",
                                minHeight: isFullscreen ? 0 : "auto",
                                "& .RaRichTextInput-root": {
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                },
                                "& .RaRichTextInput-editorContent": {
                                    flex: 1,
                                    maxHeight: isFullscreen ? "none" : "500px",
                                    overflowY: "auto",
                                },
                                "& .ProseMirror": {
                                    flex: 1,
                                    minHeight: isFullscreen ? "100%" : "200px",
                                    maxHeight: isFullscreen ? "none" : "500px",
                                    overflowY: "auto",
                                    "& img": {
                                        maxWidth: "100%",
                                        height: "auto",
                                        display: "block",
                                    },
                                },
                            }}
                        >
                            <RichTextInput
                                source="content"
                                label={" "}
                                defaultValue={mediaTools.record?.content}
                                fullWidth
                                title={translate("equip.content")}
                                toolbar={<RichtextInputActionBar mediaImages={mediaImages} />}
                            />
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pb: 2,
                        pt: 2,
                        flexShrink: 0,
                    }}
                >
                    <Box>
                        <SaveButton />
                        {mediaTools.record &&
                            permissions.vote({
                                resource: "media",
                                attribute: "delete",
                                subject: mediaTools.record,
                            }) && (
                                <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    onClick={(e) => deleteMedia(e)}
                                    startIcon={<DeleteIcon />}
                                    title={translate("equip.delete")}
                                    aria-label={translate("equip.delete")}
                                    sx={{ ml: 3 }}
                                >
                                    {translate("ra.action.delete")}
                                </Button>
                            )}
                    </Box>
                    <Button
                        onClick={() =>
                            setMediaTools({
                                edit: null,
                                type: null,
                                id: null,
                                record: null,
                            })
                        }
                        title={translate("equip.close")}
                        aria-label={translate("equip.close")}
                    >
                        {translate("equip.close")}
                    </Button>
                </Box>
            </Box>
        </Form>
    );
};
